import React, { ReactElement } from 'react';
import { WhatsaapTemplate } from 'services/whatsaapTemplates/types';
import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { fillTemplate } from 'services/whatsaapTemplates/utils';
import { RadioGroup } from 'components/RadioGroup';
import { AnchorText } from 'components/Text';
import { openSupportUrl } from 'core/utils/supportUrl';
import { FieldWrapper } from 'common/form';

const bubbleStyle: SxProps = {
  borderRadius: '0px 8px 8px 8px',
  p: 2,
  width: 280,
  mb: 2
};

type Props = {
  templates: WhatsaapTemplate[];
  templateIdSelected: string;
  onSelect: (template: WhatsaapTemplate) => void;
  disabled: boolean;
  error: string | undefined;
};

const Bubble = ({
  content,
  isSelected
}: {
  content: string | ReactElement | null;
  isSelected: boolean;
}) => {
  const {
    palette: { customGrey }
  } = useTheme();

  const additionalStyle = isSelected
    ? {
        backgroundColor: customGrey.medium
      }
    : {
        backgroundColor: 'white',
        borderColor: customGrey.medium,
        borderWith: '1px',
        borderStyle: 'solid'
      };

  return (
    <Box sx={{ ...bubbleStyle, ...additionalStyle }}>
      <Typography variant="body1" whiteSpace="pre-line">
        {content}
      </Typography>
    </Box>
  );
};

const placeholderValues = {
  firstName: '{{first name}}',
  agentName: '{{agent name}}',
  companyName: '{{company name}}',
  industry: '{{relevant sector}}',
  currentRoleCompanyName: '{{current business}}',
  jobTitle: '{{job title}}',
  employerName: '{{employer name}}',
  chatUrl: '{{chat url}}',
  location: '{{location}}'
};

export const WhatsaapTemplatesList: React.FC<Props> = ({
  templates,
  templateIdSelected,
  onSelect,
  disabled,
  error
}) => {
  return (
    <FieldWrapper
      label={
        <Box display="flex" gap={0.5}>
          <Typography variant="body2">
            Pre-approved for use with this account.
          </Typography>
          <AnchorText
            onClick={() => {
              openSupportUrl({
                ticketForm: 'request_a_change_to_opening_messages'
              });
            }}
          >
            Request a change
          </AnchorText>
        </Box>
      }
      errorMessage={error}
      labelError={!!error}
    >
      <RadioGroup
        key="template-radio-group"
        name="template"
        options={templates.map((template) => {
          const isSelected = template.id === templateIdSelected;

          const values = template.mapping.reduce((acc, mappingItem) => {
            const value =
              placeholderValues[
                mappingItem.key as keyof typeof placeholderValues
              ];

            if (value) {
              return {
                ...acc,
                [mappingItem.key]: value
              };
            }

            return acc;
          }, {});

          const contentFilled = fillTemplate(
            template.content,
            template.mapping,
            values
          );

          return {
            value: template.id,
            label: <Bubble content={contentFilled} isSelected={isSelected} />,
            disabled: !!disabled
          };
        })}
        onChange={(_, value) => {
          const selectedTemplate = templates.find(
            (template) => template.id === value
          );

          if (selectedTemplate) {
            onSelect(selectedTemplate);
          }
        }}
        value={templateIdSelected}
        formSx={{
          alignItems: 'flex-start'
        }}
        radioSx={{ p: 0, mr: 1, ml: 1 }}
        containerSx={{ mt: 1 }}
      />
    </FieldWrapper>
  );
};
